/**
 * Spinner Component
 *
 * Reusable loading spinner component with multiple sizes and variants.
 * Features:
 * - Multiple size options (xs, sm, md, lg, xl)
 * - Optional label text
 * - Customizable colors
 * - Screen reader accessibility
 * - Overlay mode for blocking UI
 */

import { createSpinner as createIconSpinner, type IconSize } from '../utils/icons';

/**
 * Spinner size variants
 */
export type SpinnerSize = IconSize;

/**
 * Spinner variant for color theming
 */
export type SpinnerVariant = 'default' | 'primary' | 'muted' | 'white';

/**
 * Spinner component options
 */
export interface SpinnerOptions {
  /** Size of the spinner (default: 'md') */
  size?: SpinnerSize;
  /** Color variant (default: 'primary') */
  variant?: SpinnerVariant;
  /** Optional label to display below the spinner */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to center the spinner in its container */
  centered?: boolean;
}

/**
 * Overlay spinner options
 */
export interface OverlaySpinnerOptions extends SpinnerOptions {
  /** Optional message to display */
  message?: string;
  /** Whether the overlay should cover the full screen (default: false) */
  fullScreen?: boolean;
  /** Background opacity (default: 0.6) */
  opacity?: number;
}

/**
 * Get color class based on variant
 */
function getVariantClass(variant: SpinnerVariant): string {
  switch (variant) {
    case 'primary':
      return 'text-primary';
    case 'muted':
      return 'text-muted-foreground';
    case 'white':
      return 'text-white';
    default:
      return 'text-foreground';
  }
}

/**
 * Create a spinner element
 */
export function createSpinner(options: SpinnerOptions = {}): HTMLElement {
  const {
    size = 'md',
    variant = 'primary',
    label,
    className = '',
    centered = false,
  } = options;

  const container = document.createElement('div');
  container.className = `spinner-container inline-flex flex-col items-center gap-2 ${centered ? 'justify-center' : ''} ${className}`.trim();
  container.setAttribute('role', 'status');
  container.setAttribute('aria-label', label || 'Loading');

  // Create the spinning icon
  const spinner = createIconSpinner({
    size,
    className: getVariantClass(variant),
  });

  container.appendChild(spinner);

  // Add optional label
  if (label) {
    const labelElement = document.createElement('span');
    labelElement.className = `text-sm ${getVariantClass(variant)}`;
    labelElement.textContent = label;
    container.appendChild(labelElement);
  }

  // Add screen reader text
  const srOnly = document.createElement('span');
  srOnly.className = 'sr-only';
  srOnly.textContent = label || 'Loading...';
  container.appendChild(srOnly);

  return container;
}

/**
 * Create a button spinner (inline, smaller)
 * Used for adding loading state to buttons
 */
export function createButtonSpinner(variant: SpinnerVariant = 'white'): HTMLElement {
  return createSpinner({
    size: 'sm',
    variant,
    className: 'inline-flex',
  });
}

/**
 * Create an overlay spinner that covers a container
 */
export function createOverlaySpinner(options: OverlaySpinnerOptions = {}): HTMLElement {
  const {
    size = 'lg',
    variant = 'primary',
    message,
    fullScreen = false,
    opacity = 0.6,
    className = '',
  } = options;

  const overlay = document.createElement('div');
  overlay.className = `spinner-overlay ${fullScreen ? 'fixed' : 'absolute'} inset-0 z-50 flex flex-col items-center justify-center ${className}`.trim();
  overlay.style.backgroundColor = `oklch(0.145 0 0 / ${opacity})`;
  overlay.style.backdropFilter = 'blur(4px)';

  // Create glass card for the spinner
  const card = document.createElement('div');
  card.className = 'glass-sm p-6 rounded-xl flex flex-col items-center gap-3';

  // Add spinner
  const spinner = createSpinner({
    size,
    variant,
    centered: true,
  });
  card.appendChild(spinner);

  // Add message if provided
  if (message) {
    const messageElement = document.createElement('p');
    messageElement.className = 'text-sm text-foreground font-medium';
    messageElement.textContent = message;
    card.appendChild(messageElement);
  }

  overlay.appendChild(card);

  return overlay;
}

/**
 * Create a page loading indicator (thin bar at top of page)
 */
export function createPageLoadingBar(): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'page-loading-bar fixed top-0 left-0 right-0 z-[100] h-1 bg-transparent overflow-hidden';

  const progress = document.createElement('div');
  progress.className = 'h-full bg-primary animate-loading-bar';
  progress.style.cssText = `
    width: 30%;
    animation: loading-bar 2s ease-in-out infinite;
  `;

  bar.appendChild(progress);

  return bar;
}

/**
 * Spinner class for programmatic control
 */
export class Spinner {
  private element: HTMLElement;

  constructor(options: SpinnerOptions = {}) {
    this.element = createSpinner(options);
  }

  /**
   * Get the spinner element
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Show the spinner in a container
   */
  show(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  /**
   * Remove the spinner from its parent
   */
  hide(): void {
    this.element.remove();
  }

  /**
   * Update the spinner label
   */
  setLabel(label: string): void {
    const labelElement = this.element.querySelector('span:not(.sr-only)');
    if (labelElement) {
      labelElement.textContent = label;
    }
    const srElement = this.element.querySelector('.sr-only');
    if (srElement) {
      srElement.textContent = label;
    }
    this.element.setAttribute('aria-label', label);
  }
}

/**
 * Overlay Spinner class for programmatic control
 */
export class OverlaySpinner {
  private element: HTMLElement;

  constructor(options: OverlaySpinnerOptions = {}) {
    this.element = createOverlaySpinner(options);
  }

  /**
   * Get the overlay element
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Show the overlay in a container
   */
  show(container: HTMLElement = document.body): void {
    // Ensure container has relative positioning for absolute overlay
    if (!this.element.classList.contains('fixed')) {
      const containerStyle = window.getComputedStyle(container);
      if (containerStyle.position === 'static') {
        container.style.position = 'relative';
      }
    }

    container.appendChild(this.element);

    // Animate in
    this.element.style.opacity = '0';
    requestAnimationFrame(() => {
      this.element.style.transition = 'opacity 0.2s ease-out';
      this.element.style.opacity = '1';
    });
  }

  /**
   * Hide and remove the overlay
   */
  hide(): void {
    this.element.style.opacity = '0';
    setTimeout(() => {
      this.element.remove();
    }, 200);
  }

  /**
   * Update the message text
   */
  setMessage(message: string): void {
    const messageElement = this.element.querySelector('p');
    if (messageElement) {
      messageElement.textContent = message;
    }
  }
}

export default Spinner;
